import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from 'uuid';
import logger from "../../logger";
import { fetchUserData, getToken, tveSignOut } from "../../helpers";
import jwtDecode from "jwt-decode";
import pubsub from "@viewlift/pubsub";
import { getStorageItem, removeStorageItem, setStorageItem } from "../../helpers/storageManager";
import { registerTveUser } from "./V2TveLogin/utils";
import { pushAdobeAnalyticsData } from "../../AdobeAnalytics/AdobeExtention";

let tveApiBaseUrl = window?.app_data?.appcmsPlatform?.tveSettings?.urlScheme
const adobeVersion = window?.app_data?.appcmsPlatform?.tveSettings?.version || "v1"
const requestorID = window?.app_data?.appcmsPlatform?.tveSettings?.requestorID || "v1"
let software_statement = adobeVersion == "v1" ? window?.app_data?.appcmsPlatform?.tveSettings?.softwareStatement : window?.app_data?.appcmsPlatform?.tveSettings?.softwareStatementV2
const xDeviceInfo = {
  "osName": "web",
  "version": navigator.appVersion,
  "model": navigator.userAgent || "",  
  "os": navigator.platform || "",  
  "browser": navigator.userAgent || "",
  "applicationId": requestorID || "" 
};

const stringfyXDeviceInfo = btoa(JSON.stringify(xDeviceInfo))

// Create Axios instance
const axiosInstance = axios.create();

// Added a request interceptor to force POST requests
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.method !== 'post') {
      return Promise.reject(new Error('Only POST requests are allowed.'));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// Register client Device
const registerCurrentDevice= ()=>{
    const registerBaseUrl = `${tveApiBaseUrl}/o/client/register`;
    return axios({
      method: 'post',
      url: registerBaseUrl,
      headers:{
        "Content-Type":"application/json",
        'X-Device-Info': stringfyXDeviceInfo
      },
      data:{
        "software_statement": software_statement
      }
    }).then((res) => {
      setStorageItem('adobeConfig', {client_id: res.data.client_id, client_secret: res.data.client_secret, grant_type: res.data.grant_types})
      return res
    })
}

// Create the Auth Token
const getTokenAgainstStatement = ({grant_types, client_id, client_secret}) => {
    const getTokenBaseUrl = `${tveApiBaseUrl}/o/client/token`;
     return axios({
      method: 'post',
      url: getTokenBaseUrl,
      headers:{
        "Content-Type": "application/x-www-form-urlencoded",
        'X-Device-Info': stringfyXDeviceInfo
      },
     data:{
        grant_type:grant_types[0],
        client_id,
        client_secret
     }
    })
    .then((token) => {
        const deviceId = btoa(uuidv4());
        const expiryTimeInDays = token.data.expires_in / 86400;
        Cookies.set('TveAccessToken', token.data.access_token, {expires: expiryTimeInDays})
        localStorage.setItem('fingerprintDeviceId', deviceId)
        return true
    })
}

// Get MVPDs list against the service provider
const getConfiguration = async() => {
  let getTveAccessToken = Cookies.get('TveAccessToken')
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
  }
    return axios({
        method: 'GET',
        url: `${tveApiBaseUrl}/api/v2/${requestorID}/configuration`,
        headers: { 
          'Authorization': `Bearer ${getTveAccessToken}`,
          'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
          'X-Device-Info': stringfyXDeviceInfo
        }
    })
    .then((res) =>{
      return res?.data?.requestor?.mvpds
    })
    .catch((error) => {
      if (error.response) {
        logger('Error Code GET Config:'+ error.response.status,'info', true);
        logger('Error Message GET Config:'+  error.response?.data?.message,'info', true);
        logger('Error Data GET Config:'+ JSON.stringify(error.response.data), 'info', true);
      } else if (error.request) {
        logger('No response received GET Config:'+ error.request,'info', true);
      } else {
        logger('Axios Error GET Config:'+ error.message,'info', true);
      }
    })
}

// Creating the Authentication Session 
const createAuthenticationSession = async(data,mvpdProvider) => {
  let getTveAccessToken = Cookies.get('TveAccessToken')
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
  }
    const createSessionBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/sessions`;
    return axios({
     method: 'POST',
     url: createSessionBaseUrl,
     headers:{
       "Content-Type":"application/x-www-form-urlencoded",
       'Authorization': `Bearer ${getTveAccessToken}`,
       'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
       'X-Device-Info': stringfyXDeviceInfo
     },
    data: data
   })
   .then((res) => {
    const mvpd = res.data.mvpd;
    if(res.data.actionName == "authenticate" && res.data.reasonType == "none"){
      const authenticationURL = res.data.url
      if(authenticationURL.includes('decisions')) return
      //ADOBE ANALYTICS CALL
      pushAdobeAnalyticsData({ eventName: `Provider Sign-In:${mvpdProvider || mvpd}`, config: { type: "TVE", status : "authn",  mvpdIdentifier: mvpdProvider || mvpd, customEvent: {key : "passsignin", value : true}} })
      window.location.href = `${tveApiBaseUrl}${authenticationURL}`
       setTimeout(() => {
        pubsub.publish('apploading', false)
       }, 3000);
    }else if(res.data.actionName == "authorize" && res.data.reasonType == "authenticated"){
      pubsub.publish('apploading', true)
      const selectedMvpdId = localStorage.getItem('selectedMvpdId')
      const resourceIds = window?.app_data?.appcmsPlatform?.tveSettings?.resourceIds 
      const path = window.location.pathname.includes('checkout') ? '/' : window.location.pathname   
      retrieveProfileAgainstMvpdCode(selectedMvpdId).then((res) => {
      //ADOBE ANALYTICS CALL
      pushAdobeAnalyticsData({ eventName: `Adobe Pass:Authenticate:Success`, config: { type: "TVE", status : "authn",  mvpdIdentifier: mvpdProvider || mvpd, authorizationStatus : "Authenticated",customEvent : {key : "passauthensuccess", value : true}, tveUserId : res.data.profiles[selectedMvpdId].attributes?.userID?.value || null} })
        setStorageItem('tveProfileResponse', res.data?.profiles?.[selectedMvpdId]?.attributes)
        if(resourceIds && resourceIds.length > 0){
          decisionsPreAuthorize(selectedMvpdId).then((preAuthorizeData) => {
          const authorisedResourceIds = []
          const authorisedData = preAuthorizeData.data.decisions
          authorisedData.map((item) => {
            if(item && item.authorized){
              authorisedResourceIds.push(item.resource)
            }
          })
          registerTveUser({
            tveUserId: res.data.profiles[selectedMvpdId].attributes?.userID?.value || '',
            mvpd: selectedMvpdId,
            encryptedZip: res.data.profiles[selectedMvpdId].attributes?.zip?.value || '',
            path: path,
            resourceIds: authorisedResourceIds
          })
        })
      }else{
        registerTveUser({ 
          tveUserId: res.data.profiles[selectedMvpdId].attributes?.userID?.value || '',
          mvpd: selectedMvpdId,
          encryptedZip: res.data.profiles[selectedMvpdId].attributes?.zip?.value || '',
          path: path, 
        })
      }
      }).catch((error) => {
        pubsub.publish('apploading', false)
        console.log(error)
      })
    }
   })
   .catch((error) => {
    if (error.response) {
      logger('Error Message Create Session:'+ error.response?.data?.message,'info', true);
      logger('Error Code Create Session:'+ error.response.status,'info');
      logger('Error Data Create Session:'+ JSON.stringify(error.response.data), 'info', true);
    } else if (error.request) {
      logger('No response received Create Session:'+ error.request,'info', true);
    } else {
      logger('Axios Error Create Session:'+ error.message,'info', true);
    }
      //ADOBE ANALYTICS CALL
      pushAdobeAnalyticsData({ eventName: `Adobe Pass:Authenticate:Fail`, config: { type: "TVE", status : "authn",  mvpdIdentifier: mvpdProvider, authorizationStatus : "Not Authorized", customEvent : {key : "passauthenfail", value : true}} })
  })
}

// Retrieve profile against code create by session API
const retrieveProfileAgainstMvpdCode = async() => {
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  let getTveAccessToken = Cookies.get('TveAccessToken')
  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
  }
    const getProfileBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/profiles`;
    return axios({
     method: 'get',
     url: getProfileBaseUrl,
     headers:{
       "Content-Type":"application/json",
       'Authorization': `Bearer ${getTveAccessToken}`,
       'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
       'X-Device-Info': stringfyXDeviceInfo
     }
   })
}

// Retrieve profile against code create by session API
const logoutProfileAgainstMvpd = async(mvpvId) => {
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  let getTveAccessToken = Cookies.get('TveAccessToken')
  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
    logger('New TveAccessToken:'+ getTveAccessToken, 'info', true);
  }
    const logoutBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/logout/${mvpvId}`;
    logger('Logout API URL:', logoutBaseUrl, 'info', true);
    return axios({
     method: 'get',
     url: logoutBaseUrl,
     headers:{
       "Content-Type":"application/json",
       'Authorization': `Bearer ${getTveAccessToken}`,
       'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
       'X-Device-Info': stringfyXDeviceInfo
     },
     params: {
      redirectUrl: `${encodeURI(window?.location?.origin)}`
     }
   }).then((res) => {
    Cookies.remove('TveAccessToken')
    localStorage.removeItem('fingerprintDeviceId')
    removeStorageItem('tveProfileResponse')
    localStorage.removeItem('selectedMvpdId')
    if(res) return res
   })
   .catch((error) => {
    logger(error, 'error', true);
    logger('Error response :'+ error?.response, 'info', true);
    logger('Error Logout pathName1:'+ location?.pathname, 'info', true)
    if (error.response) {
      logger('Error Code Logout: ' + error.response.status, 'info', true);
      logger('Error Message Logout: ' + (error.response.data?.message || 'No message'), 'info', true);
      logger('Error Data Logout: ' + JSON.stringify(error.response.data), 'info', true);
      logger('Error Logout pathName2:'+ location?.pathname, 'info', true)
    } else if (error.request) {
      logger('No response received Logout:'+ JSON.stringify(error.request), 'info', true);
      logger('Error Logout pathName3:'+ location?.pathname, 'info', true)
    } else {
      logger('Error in Logout:' + JSON.stringify(error), 'info', true);
      logger('Error Logout pathName4:'+ location?.pathname, 'info', true)
    }
    return true;
  });
}

// // Check authorization of content
// const createDecisionsAuthorize = async(data, mvpd) => {
//   let getTveAccessToken = Cookies.get('TveAccessToken')
//   const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
//   if(!getTveAccessToken) {
//     await refreshTveTokenV2()
//     getTveAccessToken = Cookies.get('TveAccessToken')
//   }
//   logger('createDecisionsAuthorize Trigger POST:'+ mvpd,'info', true);
//     const decisionBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/decisions/authorize/${mvpd}`;
//     return axios({
//      method: 'POST',
//      url: decisionBaseUrl,
//      headers:{
//        "Content-Type":"application/json",
//        'Authorization': `Bearer ${getTveAccessToken}`,
//        'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
//        'X-Device-Info': stringfyXDeviceInfo
//      },
//     data: {resources: data}
//    })
//    .then((res) => {
//     return res?.data?.decisions[0]
//    })
//    .catch((error) => {
//     logger('createDecisionsAuthorize error data:','info', true);
//     logger(data, 'info', true )
//     logger('Error Logout pathName1:'+ location?.pathname, 'info', true)
//     if (error.response) {
//       if(error.response.status == 403 && error.response.data.code == "authenticated_profile_missing"){
//         tveSignOut().then(response => {
//           getToken(async(userToken) => {
//           Cookies.remove('TveAccessToken')
//           localStorage.removeItem('fingerprintDeviceId')
//           localforage.removeItem('tveProfileResponse')
//           localStorage.removeItem('selectedMvpdId')
//           localforage.removeItem('adobeConfig')
//           let decodeToken = jwtDecode(userToken);
//           let userId = decodeToken?.userId
//           fetchUserData(userId, userToken).then((response) => {
//             location?.reload();
//           })
//         })
//       })
//       }
//       logger('Error Code Authorize:' + error.response.status,'info', true);
//       logger('Error Message Authorize:' + error.response?.data?.message,'info', true);
//       logger('Error Data Authorize:' + JSON.stringify(error.response.data), 'info', true);
//       logger('Error Decisions Authorize pathName2:'+ location?.pathname, 'info', true)
//     } else if (error.request) {
//       logger('No response received Authorize:'+ error.request,'info', true);
//       logger('Error Decisions Authorize pathName3:'+ location?.pathname, 'info', true)
//     } else {
//       logger('Error Decisions Authorize:'+ JSON.stringify(error), 'info', true)
//       logger('Error Decisions Authorize pathName4:'+ location?.pathname, 'info', true)
//     }
//     return true
//   })
// }

// Refresh Token
const refreshTveTokenV2 = async() => {
  const refreshTokenBaseUrl = `${tveApiBaseUrl}/o/client/token`;
  const getAdobeConfig = getStorageItem('adobeConfig')
   return axios({
    method: 'post',
    url: refreshTokenBaseUrl,
    headers:{
      "Content-Type": "application/x-www-form-urlencoded",
      'X-Device-Info': stringfyXDeviceInfo
    },
    data:{
      grant_type: getAdobeConfig?.grant_type[0],
      client_id: getAdobeConfig?.client_id,
      client_secret: getAdobeConfig?.client_secret
   }
  })
  .then((token) => {
      const expiryTimeInDays = token.data.expires_in / 86400;
      Cookies.set('TveAccessToken', token.data.access_token, {expires: expiryTimeInDays})
      return true
  })
  .catch((error) => {
    logger(error,'info', true);
    if (error.response) {
      logger('Error Code refreshTveTokenV2:'+ error.response.status,'info', true);
      logger('Error Message refreshTveTokenV2:'+ error.response?.data?.message,'info', true);
      logger('Error Data refreshTveTokenV2:'+ JSON.stringify(error.response.data), 'info', true);
    } else if (error.request) {
      logger('No response received refreshTveTokenV2:'+ error.request,'info', true);
    } else {
      logger('Axios Error refreshTveTokenV2:'+ error.message,'info', true);
    }
  })
}

// Check the Authentication Session 
const checkAuthenticationSession = async(data) => {
  let getTveAccessToken = Cookies.get('TveAccessToken')
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
  }
    const createSessionBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/sessions`;
    return axios({
     method: 'POST',
     url: createSessionBaseUrl,
     headers:{
       "Content-Type":"application/x-www-form-urlencoded",
       'Authorization': `Bearer ${getTveAccessToken}`,
       'AP-Device-Identifier':`fingerprint ${getFingerprintDeviceId}`,
       'X-Device-Info': stringfyXDeviceInfo
     },
    data: data
   })
   .then((res) => {
    return res
   })
}

const createDecisionsAuthorize = async (data, mvpd) => {
  let getTveAccessToken = Cookies.get('TveAccessToken');
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId');

  if (!getTveAccessToken) {
    await refreshTveTokenV2();
    getTveAccessToken = Cookies.get('TveAccessToken');
  }
  logger('createDecisionsAuthorize Trigger POST:' + mvpd, 'info', true);
  const decisionBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/decisions/authorize/${mvpd}`;

  try {
    const res = await axiosInstance({
      method: 'POST',
      url: decisionBaseUrl,
      headers: {
        "Content-Type": "application/json",
        'Authorization': `Bearer ${getTveAccessToken}`,
        'AP-Device-Identifier': `fingerprint ${getFingerprintDeviceId}`,
        'X-Device-Info': stringfyXDeviceInfo
      },
      data: { resources: data }
    });
    return res?.data?.decisions[0];
  } catch (error) {
    logger('createDecisionsAuthorize error data:', 'info', true);
    logger(data, 'info', true);
    logger('Error Decisions pathName1:' + location?.pathname, 'info', true);

    if (error.response) {
      if (error.response.status === 403 && error.response.data.code === "authenticated_profile_missing") {
        tveSignOut().then(response => {
          getToken(async (userToken) => {
            Cookies.remove('TveAccessToken');
            localStorage.removeItem('fingerprintDeviceId');
            removeStorageItem('tveProfileResponse');
            localStorage.removeItem('selectedMvpdId');
            removeStorageItem('adobeConfig');
            let decodeToken = jwtDecode(userToken);
            let userId = decodeToken?.userId;
            fetchUserData(userId, userToken).then((response) => {
              location?.reload();
            });
          });
        });
      }

      logger('Error Code Authorize:' + error.response.status, 'info', true);
      logger('Error Message Authorize:' + error.response?.data?.message, 'info', true);
      logger('Error Data Authorize:' + JSON.stringify(error.response.data), 'info', true);
      logger('Error Decisions Authorize pathName2:' + location?.pathname, 'info', true);
    } else if (error.request) {
      logger('No response received Authorize:' + error.request, 'info', true);
      logger('Error Decisions Authorize pathName3:' + location?.pathname, 'info', true);
    } else {
      logger('Error Decisions Authorize:' + JSON.stringify(error), 'info', true);
      logger('Error Decisions Authorize pathName4:' + location?.pathname, 'info', true);
    }
    throw error;
  }
};

function decisionResourceIds() {
  const resourceIds = window?.app_data?.appcmsPlatform?.tveSettings?.resourceIds || [] 
  let result = [];
  for (let i = 0; i < resourceIds.length; i += 5) {
    result.push(resourceIds.slice(i, i + 5));
  }
  return result;
}

// PreAuthorise call against resourceIds
const decisionsPreAuthorize = async(mvpd) => {
  let getTveAccessToken = Cookies.get('TveAccessToken')
  const resourceIds = window?.app_data?.appcmsPlatform?.tveSettings?.resourceIds || [] 
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  const decisionResource = decisionResourceIds()

  if(!getTveAccessToken) {
    await refreshTveTokenV2()
    getTveAccessToken = Cookies.get('TveAccessToken')
  }
  const preAuthoriseBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/decisions/preauthorize/${mvpd}`;
  return Promise.all(decisionResource.map(resource =>
    axios({
      method: 'POST',
      url: preAuthoriseBaseUrl,
      headers: {
        "Content-Type": "application/json",
        'Authorization': `Bearer ${getTveAccessToken}`,
        'AP-Device-Identifier': `fingerprint ${getFingerprintDeviceId}`,
        'X-Device-Info': stringfyXDeviceInfo
      },
      data: { resources: resource }
    })
    ))
    .then(results => {
       const combineDecisionData = [];
      results.forEach((res) => {
        combineDecisionData.push(res.data.decisions);
      });

      const flatArr = combineDecisionData.flat();
      return flatArr; 
      })
    .catch(error => {
      logger('decision preauthorise ' + error, 'info', true);
  });
    
}
// Creating the Authentication Session 
const createTveAuthSession = async(data, mvpdProvider) => {
  let getTveAccessTokenCtv = Cookies.get('Tve_Access_token')
  const getFingerprintDeviceId = localStorage.getItem('fingerprintDeviceId')
  if(!getTveAccessTokenCtv) {
    await refreshTveTokenV2()
    getTveAccessTokenCtv = Cookies.get('Tve_Access_token')
  }
    const createSessionBaseUrl = `${tveApiBaseUrl}/api/v2/${requestorID}/sessions/${mvpdProvider}`;
    return axios({
     method: 'POST',
     url: createSessionBaseUrl,
     headers:{
       "Content-Type":"application/x-www-form-urlencoded",
       'Authorization': `Bearer ${getTveAccessTokenCtv}`,
     },
    data: data
   }).then((res) => {
    if(res.data.actionName == "authenticate" && res.data.reasonType == "none"){
      const authenticationURL = res.data.url
      if(authenticationURL.includes('decisions')) return
      sessionStorage.removeItem('tveRegCode')
      window.location.href = `${tveApiBaseUrl}${authenticationURL}`
    }else if(res.data.actionName == "authorize" && res.data.reasonType == "authenticated"){
      sessionStorage.removeItem('tveRegCode')
      window.location.href = window.location.origin
    }
   })
   .catch((error) => {
    if (error.response) {
      logger('Error Message Create Session:'+ error.response?.data?.message,'info', true);
      logger('Error Code Create Session:'+ error.response.status,'info');
      logger('Error Data Create Session:'+ JSON.stringify(error.response.data), 'info', true);
    } else if (error.request) {
      logger('No response received Create Session:'+ error.request,'info', true);
    } else {
      logger('Axios Error Create Session:'+ error.message,'info', true);
    }
  })
}

export {registerCurrentDevice, getTokenAgainstStatement, getConfiguration, createAuthenticationSession, retrieveProfileAgainstMvpdCode, logoutProfileAgainstMvpd, createDecisionsAuthorize, refreshTveTokenV2, checkAuthenticationSession, decisionsPreAuthorize, createTveAuthSession,decisionResourceIds}
